import { IAggFuncParams } from "ag-grid-community";
import Enumerable from "linq";
import { GridColumnAggregateEnum } from "../../common/enums/GridColumnAggregateEnum";
import { round } from "lodash";
import { NumberParser } from "../../common/utilities/NumberParser";

export type AggregateValue = {
    aggregate: GridColumnAggregateEnum;
    value?: number;
    decimalPlaces?: number;
};

export type AggregateFunction = (params: IAggFuncParams) => AggregateValue;

const defaultDecimals = 2;
const parser = new NumberParser(navigator.language)

export function getAggregateFunction(aggregate?: GridColumnAggregateEnum, decimalPlaces: number = defaultDecimals): AggregateFunction | null {
    switch (aggregate) {
        case GridColumnAggregateEnum.Sum:
            return (params: IAggFuncParams) => _calculateSum(params, decimalPlaces);
        case GridColumnAggregateEnum.Count:
            return _calculateCount;
        case GridColumnAggregateEnum.Average:
            return (params: IAggFuncParams) => _calculateAverage(params, decimalPlaces);
        case GridColumnAggregateEnum.Min:
            return (params: IAggFuncParams) => _calculateMin(params, decimalPlaces);
        case GridColumnAggregateEnum.Max:
            return (params: IAggFuncParams) => _calculateMax(params, decimalPlaces);
        default:
            // Need to return null instead of undefined. aggFunc is treated as state
            // and will not be reset when columns are updated if undefined is returned.
            // Ref: https://www.ag-grid.com/javascript-grid/column-updating-definitions/
            return null;
    }
}

function _calculateSum(params: IAggFuncParams, decimalPlaces: number): AggregateValue {
    const filteredValues = Enumerable
        .from(params.values)
        .select(value => parser.parse(value))
        .where(value => Number.isFinite(value))
        .toArray();

    const count = filteredValues.length;

    if (count > 0) {
        let sum = Enumerable.from(filteredValues).sum();
        sum = round(sum, decimalPlaces);

        return {
            aggregate: GridColumnAggregateEnum.Sum,
            value: sum,
            decimalPlaces: decimalPlaces
        };
    }

    return {
        aggregate: GridColumnAggregateEnum.Sum,
        value: undefined,
        decimalPlaces: undefined
    };
}

function _calculateAverage(params: IAggFuncParams, decimalPlaces: number): AggregateValue {
    const filteredValues = Enumerable
        .from(params.values)
        .select(value => parser.parse(value))
        .where(value => Number.isFinite(value))
        .toArray();
    
    const count = filteredValues.length;

    if (count > 0) {
        let avg = Enumerable.from(filteredValues).average();
        avg = round(avg, decimalPlaces);

        return {
            aggregate: GridColumnAggregateEnum.Average,
            value: avg,
            decimalPlaces: decimalPlaces
        };
    }

    return {
        aggregate: GridColumnAggregateEnum.Average,
        value: undefined,
        decimalPlaces: undefined
    };
}

function _calculateCount(params: IAggFuncParams): AggregateValue {
    const count = Enumerable
        .from(params.values)
        .count(value => value !== undefined && value !== null);

    if (count > 1) {
        return {
            aggregate: GridColumnAggregateEnum.Count,
            // TODO: Need to calculate totals row differently            
            // value: (params.rowNode.level === -1)
            //     ? Enumerable.from(filteredValues).select(aggregateValue => aggregateValue.value).sum()
            //     : count
            value: count,
            decimalPlaces: 0
        };
    }

    return {
        aggregate: GridColumnAggregateEnum.Count,
        value: undefined,
        decimalPlaces: 0
    };
}

function _calculateMin(params: IAggFuncParams, decimalPlaces: number): AggregateValue {
    const filteredValues = Enumerable
        .from(params.values)
        .select(value => parser.parse(value))
        .where(value => Number.isFinite(value))
        .toArray();

    const count = filteredValues.length;

    if (count > 0) {
        let min = Enumerable.from(filteredValues).min();
        min = round(min, decimalPlaces);

        return {
            aggregate: GridColumnAggregateEnum.Min,
            value: min,
            decimalPlaces: decimalPlaces
        };
    }

    return {
        aggregate: GridColumnAggregateEnum.Min,
        value: undefined,
        decimalPlaces: undefined
    };
}

function _calculateMax(params: IAggFuncParams, decimalPlaces: number): AggregateValue {
    const filteredValues = Enumerable
        .from(params.values)
        .select(value => parser.parse(value))
        .where(value => Number.isFinite(value))
        .toArray();

    const count = filteredValues.length;

    if (count > 0) {
        let max = Enumerable.from(filteredValues).max();
        max = round(max, decimalPlaces);

        return {
            aggregate: GridColumnAggregateEnum.Max,
            value: max,
            decimalPlaces: decimalPlaces
        };
    }

    return {
        aggregate: GridColumnAggregateEnum.Max,
        value: undefined,
        decimalPlaces: undefined
    };
}